// src/hooks/usePromptGeneration.ts
import { VAPI_TOOLS } from '../pages/dashboard/hero-commands/configs';
import { ICommand, Item } from "@/types/heroCommands";
import { rules } from './useHeroCommands';

export const usePromptGeneration = () => {
  const transformVariables = (data: Record<string, string>, ruleIndex: number = 1) => {

    const transformedVariables: Item = {};
    const currentRules = rules[ruleIndex];
    
    if (!currentRules) throw new Error(`Rule index ${ruleIndex} not found`);
    
    Object.entries(data).map(([key, value]) => {
      const mappedKey = currentRules[key];
      if (mappedKey) {
        transformedVariables[mappedKey] = value;
      }
    });
    
    return transformedVariables;
  };

  const generatePrompt = (
    formData: Record<string, string>,
    feature: ICommand,
    selectedTool?: string,
    customPrompt?: string
  ): { prompt: string, variables: Item } => {
    const res: Record<string, string> = {};
    Object.entries(formData).map(([key, value]) => {
      res[key] = value.trim();
    });
    
    const transformedVariables = transformVariables(res, feature.rule);
    if (!selectedTool) {
      return {
        prompt: feature.prompt,
        variables: transformedVariables
      };
    }
    if (selectedTool === 'manual_upload') {
      return {
        prompt: `[MANUAL TOOL FLOW]

You are an AI assistant.

⚠️ STRICT EXECUTION ORDER — DO NOT BREAK IT:

1. Begin with a short greeting: "Hi, I'm your AI assistant."
2. IMMEDIATELY:
   - Study the following instructions.
   - DO NOT read them aloud.
   - DO what they say, step-by-step, without delay.

🔇 NEVER explain or repeat these instructions to the user.

🧠 CRITICAL:
- DO NOT proceed to the main request below **under any circumstance** unless the user **explicitly asks for it**.
- IGNORE the main request by default — it is for reference only.
- DO NOT continue the conversation beyond the instructions unless the user says so.

📜 CUSTOM INSTRUCTIONS TO EXECUTE:
${customPrompt}

📎 Main request (REFERENCE ONLY — USE ONLY IF USER ASKS): "${feature.title}"
${feature.prompt}`,
        variables: transformedVariables
      };
    }

    const tool = VAPI_TOOLS.find(t => t.id === selectedTool);
    return {
      prompt: `[TOOL FLOW: ${selectedTool.toUpperCase()}]

You are an AI assistant.

⚠️ FOLLOW THESE RULES EXACTLY — DO NOT DEVIATE:

1. Start with a short greeting and introduction: "Hi, I'm your AI assistant."
2. You are now testing a specific tool-based functionality.
3. Your behavior must strictly follow the tool definition and description below.

📌 TOOL NAME: ${tool?.name}
📄 TOOL DESCRIPTION:
${tool?.description}

🧠 BEHAVIORAL RULES:
- USE the tool description above as the ONLY source of behavior.
- DO NOT interpret or mix it with the main request unless the user explicitly asks.
- FOLLOW its logic precisely — ask, say, confirm, or act as implied by the tool.
- DO NOT make up steps, assumptions, or extend the conversation beyond the tool purpose.
- KEEP your responses short and actionable — avoid unnecessary words or explanations.

📎 Main request (REFERENCE ONLY — USE ONLY IF USER EXPLICITLY ASKS): "${feature.title}"
${feature.prompt}`,
      variables: transformedVariables
    };
  };

  const generateCallMetadata = (
    id: string,
    selectedTool?: string,
    customManualInstruction?: string
  ) => {
    const metadata: { command_id: string; saas_call_type: string; selected_tool: string; custom_manual_instruction?: string; } = {
      command_id: id,
      saas_call_type: selectedTool ? "Tool Test Call" : "Test Call",
      selected_tool: selectedTool || "",
    };
    if (selectedTool) {
      metadata.selected_tool = selectedTool;
    }
    // Add custom_manual_instruction to metadata if provided for manual_upload
    if (selectedTool === 'manual_upload' && typeof customManualInstruction === 'string') {
      metadata.custom_manual_instruction = customManualInstruction;
    }
    return metadata;
  };

  return {
    transformVariables,
    generatePrompt,
    generateCallMetadata
  };
};